import { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Check, AlertTriangle, Send, Loader2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { shortTime } from '../lib/format.js';

/**
 * Whether notifications are actually arriving.
 *
 * Push and Slack leave through networks nobody here controls, so "we sent it"
 * and "they got it" are different claims — this card is where the difference is
 * readable. It is deliberately failure-shaped: the counts sit at the top
 * because most of the time the honest answer is "everything got through", and
 * a list of successful deliveries would bury the two that did not.
 *
 * `Recovered` is the number worth watching. Those are notifications that were
 * lost before the retry queue existed.
 */

const CHANNEL_LABEL = { push: 'Browser push', slack: 'Slack' };

/** Provider codes are precise but unreadable; this is what they mean. */
const ERROR_HELP = {
  http_404: 'The browser is no longer subscribed',
  http_410: 'The browser is no longer subscribed',
  http_401: 'The push service rejected the VAPID keys',
  http_403: 'The push service rejected the VAPID keys',
  http_413: 'The notification payload was too large',
  network_error: 'Could not reach the push service',
  subscription_removed: 'The device was unregistered before this could be sent',
  push_disabled: 'Web push is not configured on this server',
  slack_disconnected: 'No Slack channel is connected to this app',
  invalid_auth: 'Slack rejected the bot token',
  token_revoked: 'The Slack bot token was revoked',
  channel_not_found: 'The Slack channel no longer exists',
  not_in_channel: 'The bot is not a member of that channel',
  is_archived: 'The Slack channel is archived',
  ratelimited: 'Slack rate-limited us',
  timeout: 'Slack did not answer in time',
};

const STATUS_CLASS = {
  pending: 'pill pill--warn',
  sent: 'pill pill--ok',
  failed: 'pill pill--muted',
  dead: 'pill pill--muted',
};

const STATUS_LABEL = {
  pending: 'Retrying',
  sent: 'Delivered',
  failed: 'Gave up',
  dead: 'Undeliverable',
};

function Counts({ label, stats }) {
  if (!stats || stats.total === 0) {
    return (
      <div className="settings__row">
        <span>{label}</span>
        <span className="mono muted">Nothing sent yet</span>
      </div>
    );
  }

  const problems = stats.pending + stats.failed + stats.dead;

  return (
    <div className="settings__row">
      <span>{label}</span>
      <span className="settings__control">
        <span className="mono muted">{stats.sent} delivered</span>
        {problems > 0 && (
          <span className={`pill ${stats.failed + stats.dead > 0 ? 'pill--warn' : 'pill--muted'}`}>
            {stats.pending > 0 && `${stats.pending} retrying`}
            {stats.pending > 0 && stats.failed + stats.dead > 0 && ' · '}
            {stats.failed + stats.dead > 0 && `${stats.failed + stats.dead} not delivered`}
          </span>
        )}
      </span>
    </div>
  );
}

export function DeliveryStatusCard({ appId, canEdit }) {
  const [stats, setStats] = useState(null);
  const [problems, setProblems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(async () => {
    if (!appId) return;
    setError(null);
    try {
      const { data, meta } = await api.deliveries.list(appId, { problems: 'true', limit: 8 });
      setProblems(Array.isArray(data) ? data : []);
      setStats(meta?.stats ?? null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const retry = async (delivery) => {
    setRetrying(delivery.id);
    setError(null);
    try {
      await api.deliveries.retry(appId, delivery.id);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setRetrying(null);
    }
  };

  return (
    <div className="card">
      <div className="card__head">
        <span className="card__title">
          <Send size={13} /> Notification delivery
        </span>
        <button type="button" className="link-button" onClick={load} disabled={loading}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="card__foot">
        Push and Slack are the two channels that leave through someone else&apos;s network. A
        failed send is retried three times with a widening gap; anything a retry cannot fix — a
        browser that unsubscribed, a revoked Slack token — stops immediately and is listed here
        instead. Last 7 days.
      </div>

      <div className="settings">
        <Counts label={CHANNEL_LABEL.push} stats={stats?.push} />
        <Counts label={CHANNEL_LABEL.slack} stats={stats?.slack} />

        {stats?.recovered > 0 && (
          <div className="settings__row">
            <span>
              Recovered by retry
              <span className="muted"> — would have been lost before</span>
            </span>
            <span className="mono">{stats.recovered}</span>
          </div>
        )}
      </div>

      {error && <div className="login__error">{error}</div>}

      {loading ? (
        <div className="muted">
          <Loader2 size={13} className="spin" /> Loading…
        </div>
      ) : problems.length === 0 ? (
        <div className="result result--ok">
          <Check size={13} /> Everything got through.
        </div>
      ) : (
        <div className="delivery-list">
          {problems.map((delivery) => (
            <div className="delivery" key={delivery.id}>
              <span className={STATUS_CLASS[delivery.status]}>
                {STATUS_LABEL[delivery.status]}
              </span>
              <span className="delivery__what">
                <span className="delivery__channel">{CHANNEL_LABEL[delivery.channel]}</span>
                <span className="delivery__target mono">{delivery.target}</span>
              </span>
              <span className="delivery__why" title={delivery.lastError}>
                {ERROR_HELP[delivery.errorCode] ?? delivery.lastError ?? 'Unknown error'}
              </span>
              <span className="delivery__when mono muted">
                {delivery.attempts}/{delivery.maxAttempts} · {shortTime(delivery.createdAt)}
              </span>
              {/* Only what has stopped: something still queued has attempts of
                  its own left, and jogging it would consume one. */}
              {canEdit && delivery.status !== 'pending' && (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={() => retry(delivery)}
                  disabled={retrying === delivery.id}
                >
                  {retrying === delivery.id ? 'Sending…' : 'Send again'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {problems.some((d) => d.status === 'dead') && (
        <div className="card__foot">
          <AlertTriangle size={12} /> An undeliverable push usually means that browser cleared its
          site data or revoked permission — the agent re-enables it from this page on the device
          itself. An undeliverable Slack post means the connection needs fixing above.
        </div>
      )}
    </div>
  );
}

export default DeliveryStatusCard;
